import React, { useState } from 'react';
import { AlertCircle, CheckCircle } from 'lucide-react';

const ConsentForm = ({ onAccept, onCancel, customerName = "" }) => {
  const [formData, setFormData] = useState({
    fullName: customerName,
    agreedToTerms: false,
    agreedToHipaa: false,
    agreedToPayment: false,
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = "Please enter your full name";
    }
    
    if (!formData.agreedToTerms) {
      newErrors.terms = "You must agree to the treatment consent";
    }
    
    if (!formData.agreedToHipaa) {
      newErrors.hipaa = "You must acknowledge the privacy notice";
    }
    
    if (!formData.agreedToPayment) {
      newErrors.payment = "You must agree to the payment terms";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onAccept({
        signature: formData.fullName,
        timestamp: new Date().toISOString(),
        agreedToAll: true
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-gray-900 rounded-2xl max-w-4xl w-full my-8 border border-gray-800">
        {/* Header */}
        <div className="bg-gradient-to-r from-yellow-400/10 to-yellow-600/10 border-b border-gray-800 p-6">
          <h2 className="text-3xl font-bold text-white mb-2">Consent & Agreement</h2>
          <p className="text-gray-300">Please review and sign the following consent forms</p>
        </div>

        {/* Scrollable Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto space-y-6">
          
          {/* Section 1: Treatment Consent & Patient Rights */}
          <div className="bg-black/30 rounded-xl p-6 border border-gray-800">
            <h3 className="text-xl font-bold text-yellow-400 mb-4 flex items-center">
              <CheckCircle className="w-6 h-6 mr-2" />
              Treatment Consent & Patient Rights
            </h3>
            <div className="text-gray-300 space-y-3 text-sm leading-relaxed">
              <p>
                <strong className="text-white">Informed Consent for IV Therapy:</strong> I hereby authorize EliteIV Lounge and its healthcare providers to administer intravenous (IV) therapy, including vitamins, minerals, medications, and hydration as prescribed. I understand the nature of the treatment, potential benefits, and possible risks including but not limited to infection, bruising, allergic reactions, phlebitis, and rare serious complications.
              </p>
              <p>
                <strong className="text-white">Patient Rights:</strong> I understand that I have the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Receive respectful, considerate care</li>
                <li>Be informed of my diagnosis, treatment, and prognosis</li>
                <li>Refuse treatment or withdraw consent at any time</li>
                <li>Privacy and confidentiality of my medical information</li>
                <li>Access my medical records upon request</li>
                <li>Voice concerns or complaints without fear of retaliation</li>
                <li>Be informed of any changes to my care plan</li>
              </ul>
              <p>
                <strong className="text-white">Acknowledgment:</strong> I have been given the opportunity to ask questions about my treatment and my rights as a patient. I understand that no guarantees have been made regarding the outcome of treatments.
              </p>
            </div>
            <div className="mt-4">
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.agreedToTerms}
                  onChange={(e) => setFormData({...formData, agreedToTerms: e.target.checked})}
                  className="mt-1 w-5 h-5 rounded border-gray-600 text-yellow-400 focus:ring-yellow-400 focus:ring-offset-gray-900"
                />
                <span className="text-white font-medium">
                  I have read and agree to the Treatment Consent and acknowledge my Patient Rights
                </span>
              </label>
              {errors.terms && (
                <p className="text-red-400 text-sm mt-2 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.terms}
                </p>
              )}
            </div>
          </div>

          {/* Section 2: HIPAA Privacy Notice */}
          <div className="bg-black/30 rounded-xl p-6 border border-gray-800">
            <h3 className="text-xl font-bold text-yellow-400 mb-4 flex items-center">
              <CheckCircle className="w-6 h-6 mr-2" />
              HIPAA Privacy Notice
            </h3>
            <div className="text-gray-300 space-y-3 text-sm leading-relaxed">
              <p>
                <strong className="text-white">Notice of Privacy Practices:</strong> This notice describes how medical information about you may be used and disclosed and how you can access this information.
              </p>
              <p>
                <strong className="text-white">How We Use Your Information:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Treatment:</strong> To provide, coordinate, and manage your healthcare</li>
                <li><strong>Payment:</strong> To bill and collect payment for services</li>
                <li><strong>Healthcare Operations:</strong> To improve quality of care and business operations</li>
              </ul>
              <p>
                <strong className="text-white">Your Privacy Rights:</strong> You have the right to request restrictions on how we use/disclose your information, inspect and obtain copies of your records, request amendments to your records, receive an accounting of disclosures, and file a complaint if you believe your privacy rights have been violated.
              </p>
              <p>
                <strong className="text-white">Information Security:</strong> We maintain physical, electronic, and procedural safeguards to protect your health information. We will not sell your information or use it for marketing without your authorization.
              </p>
            </div>
            <div className="mt-4">
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.agreedToHipaa}
                  onChange={(e) => setFormData({...formData, agreedToHipaa: e.target.checked})}
                  className="mt-1 w-5 h-5 rounded border-gray-600 text-yellow-400 focus:ring-yellow-400 focus:ring-offset-gray-900"
                />
                <span className="text-white font-medium">
                  I acknowledge receipt of the HIPAA Privacy Notice and understand how my information will be used
                </span>
              </label>
              {errors.hipaa && (
                <p className="text-red-400 text-sm mt-2 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.hipaa}
                </p>
              )}
            </div>
          </div>

          {/* Section 3: Payment Terms & Financial Agreement */}
          <div className="bg-black/30 rounded-xl p-6 border border-gray-800">
            <h3 className="text-xl font-bold text-yellow-400 mb-4 flex items-center">
              <CheckCircle className="w-6 h-6 mr-2" />
              Payment Terms & Financial Agreement
            </h3>
            <div className="text-gray-300 space-y-3 text-sm leading-relaxed">
              <p>
                <strong className="text-white">Payment Policy:</strong> Payment is due at the time of service unless prior arrangements have been made. We accept cash, credit cards, and debit cards.
              </p>
              <p>
                <strong className="text-white">Pricing & Services:</strong> Service prices are available upon request and may vary based on the specific treatment, add-ons, and customizations selected. I understand that I am financially responsible for all services rendered.
              </p>
              <p>
                <strong className="text-white">Membership & Packages:</strong> If enrolled in a membership or package plan, I understand the terms including:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Membership fees are non-refundable</li>
                <li>Unused treatments expire according to plan terms</li>
                <li>Membership benefits cannot be transferred to others</li>
                <li>Additional services beyond membership benefits will be charged separately</li>
              </ul>
              <p>
                <strong className="text-white">Cancellation Policy:</strong> Appointments cancelled with less than 24 hours notice may be subject to a cancellation fee or forfeiture of prepaid services.
              </p>
              <p>
                <strong className="text-white">Insurance:</strong> EliteIV Lounge services are typically not covered by insurance. I understand I am responsible for full payment regardless of any insurance I may have.
              </p>
            </div>
            <div className="mt-4">
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.agreedToPayment}
                  onChange={(e) => setFormData({...formData, agreedToPayment: e.target.checked})}
                  className="mt-1 w-5 h-5 rounded border-gray-600 text-yellow-400 focus:ring-yellow-400 focus:ring-offset-gray-900"
                />
                <span className="text-white font-medium">
                  I agree to the Payment Terms and understand my financial responsibilities
                </span>
              </label>
              {errors.payment && (
                <p className="text-red-400 text-sm mt-2 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.payment}
                </p>
              )}
            </div>
          </div>

          {/* Signature Section */}
          <div className="bg-gradient-to-br from-yellow-400/10 to-transparent rounded-xl p-6 border-2 border-yellow-400/30">
            <h3 className="text-xl font-bold text-white mb-4">Electronic Signature</h3>
            <p className="text-gray-300 mb-4 text-sm">
              By typing your full name below, you are providing your electronic signature and agreeing to all terms above.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Type Your Full Name *
              </label>
              <input
                type="text"
                value={formData.fullName}
                onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                placeholder="John Doe"
              />
              {errors.fullName && (
                <p className="text-red-400 text-sm mt-2 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.fullName}
                </p>
              )}
            </div>
            <p className="text-gray-400 text-xs mt-3">
              Date: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="border-t border-gray-800 p-6 flex flex-col sm:flex-row gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white font-semibold rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-8 py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-black font-bold rounded-lg transition-all"
          >
            Sign & Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsentForm;
